import { useEffect } from 'react';
/* eslint-disable no-console */
import scrollToElement from 'scroll-to-element';
import { withPrefix } from 'gatsby';
import * as errorTypes from './scrollErrors';

export const isBrowser = typeof window !== 'undefined';

export const isDevelopment = process.env.NODE_ENV !== 'production';

export function logWarning(message) {
  if (isDevelopment) console.warn(message);
}

export function scroller(target, offset = 0, duration = 1000) {
  scrollToElement(target, {
    duration,
    offset,
  });
}

export function handleLinkClick(to, e, onAnchorLinkClick) {
  /**
   * Log warnings on click
   */
  const improperFormatting = !to.includes('/') || !to.includes('#');
  if (improperFormatting) logWarning(errorTypes.IMPROPPER_FORMATTING);

  if (isBrowser && to.includes('#')) {
    const [anchorPath, anchor] = to.split('#');
    if (window.location.pathname === withPrefix(anchorPath)) {
      e.preventDefault();
      scroller(
        `#${anchor}`,
        window.gatsby_scroll_offset,
        window.gatsby_scroll_duration,
      );
    }
  }

  if (onAnchorLinkClick) onAnchorLinkClick();
}

export function handleStrippedLinkClick(to, e, onAnchorLinkClick) {
  /**
   * Log warnings on click
   */
  const improperFormatting = !to.includes('/') || !to.includes('#');
  if (improperFormatting) logWarning(errorTypes.IMPROPPER_FORMATTING);

  const [anchorPath, anchor] = to.split('#');

  /**
   * Determine location, run scroller or set window variable
   */

  const isSamePage = isBrowser && window.location.pathname === anchorPath;
  const isDifferentPage = isBrowser && window.location.pathname !== anchorPath;

  if (isSamePage) {
    e.preventDefault();
    scroller(
      `#${anchor}`,
      window.gatsby_scroll_offset,
      window.gatsby_scroll_duration,
    );
  }

  if (isDifferentPage) {
    window.gatsby_scroll_hash = `#${anchor}`;
  }

  if (onAnchorLinkClick) onAnchorLinkClick();
}

export function stripHashedLocation(to) {
  return to.split('#')[0];
}

export function checkHash(location, offset) {
  const { hash } = location,
    selector = hash ? hash.substr(1) : null,
    validElement = selector ? document.getElementById(selector) : null;
  if (hash && Boolean(validElement)) scroller(hash, offset);
  else if (hash && selector && !validElement)
    logWarning(errorTypes.INVALID_HASH);
}

// Scrolls to #id once the section that owns it has mounted, so it works after page
// transitions and on first load without guessing timings. Re-checks once for late layout shifts.
export function useScrollToHash(id) {
  useEffect(() => {
    if (!id || window.location.hash !== `#${id}`) return undefined;
    const jump = () => {
      const el = document.getElementById(id);
      if (el) {
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY,
          behavior: 'instant',
        });
      }
    };
    const frame = window.requestAnimationFrame(jump);
    // After gatsby-browser's scroll-to-top, which runs 1000ms into a page change;
    // skipped once the user scrolls themselves, so they are not pulled back
    const timer = window.setTimeout(jump, 1200);
    const userEvents = ['wheel', 'touchmove', 'keydown'];
    const cancelJump = () => window.clearTimeout(timer);
    userEvents.forEach((event) =>
      window.addEventListener(event, cancelJump, { passive: true, once: true }),
    );
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      userEvents.forEach((event) =>
        window.removeEventListener(event, cancelJump),
      );
    };
  }, [id]);
}
